import React from "react";
import {
  Badge,
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { isDarkMode } from "../../utils/constants";

export const MAX_REASONING_MODE = "max";
export const REASONING_MODE_LABELS = {
  standard: "Fast",
  extended: "Standard",
  deep: "Deep Thinking",
  [MAX_REASONING_MODE]: "MAX Reasoning",
};

export const displayReasoningMode = (mode, isMaxTierSubscriber) => {
  if (mode === MAX_REASONING_MODE && !isMaxTierSubscriber) return "deep";
  return REASONING_MODE_LABELS[mode] ? mode : "standard";
};

export const IconWithBadge = ({ count, children }) => {
  if (!count || count <= 0) return children;

  return (
    <Badge
      badgeContent={count}
      overlap="circular"
      color="primary"
      max={99}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {children}
    </Badge>
  );
};

const SidebarTopBar = React.memo(function SidebarTopBar({
  panel,
  setPanel,
  isDesktop,
  hasSubscription,
  showMaxUpgradeButton,
  maxUpgradeHref,
  maxUpgradeLabel,
  preAccountUser,
  eligibleForProTrial,
  isMaxTierSubscriber,
  navigate,
  setIsSidebarOpen,
  reasoningMode,
  onChangeReasoningMode,
  unreadNotificationCount,
}) {
  const panelTitleByKey = {
    watchlist: "Watchlist",
    trade_proposals: "Trade Actions",
    memory: "User Memory",
    agent_tasks: "Agent Tasks",
  };
  const activePanelTitle = panelTitleByKey[panel] || null;
  const proUpgradeLabel = eligibleForProTrial ? "Try Pro Free" : "Upgrade to Pro";
  const selectedReasoningMode = displayReasoningMode(reasoningMode, isMaxTierSubscriber);
  const upgradePalette = isDarkMode
    ? {
        background: "#131520",
        border: "#2A3C70",
        iconBg: "#2A3C70",
        text: "#EAF0FF",
        icon: "#BFD6FF",
      }
    : {
        background: "#EEF3FF",
        border: "#BFD0F2",
        iconBg: "#2D61CF",
        text: "#1A3258",
        icon: "#FFFFFF",
      };
  const maxUpgradePalette = isDarkMode
    ? {
        background: "rgba(47, 143, 91, 0.06)",
        border: "#2E6A4F",
        iconBg: "#1F7A52",
        text: "#DDF7E8",
        icon: "#FFFFFF",
        hoverBackground: "rgba(47, 143, 91, 0.1)",
        hoverBorder: "#3E8A68",
      }
    : {
        background: "rgba(47, 143, 91, 0.04)",
        border: "#B7DFC6",
        iconBg: "#2F8F5B",
        text: "#1C5B39",
        icon: "#FFFFFF",
        hoverBackground: "rgba(47, 143, 91, 0.08)",
        hoverBorder: "#96CDAE",
      };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: panel === "chat" ? "space-between" : "flex-start",
        gap: 1.5,
        px: 1.25,
        py: 1,
        bgcolor: isDarkMode ? "#222222" : "#F9F9F9",
        borderBottom: "1px solid",
        borderColor: isDarkMode ? "#2f2f2f" : "#e7e7e7",
      }}
    >
      {panel !== "chat" ? (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flex: 1,
            justifyContent: "flex-start",
          }}
        >
          <IconButton
            onPointerDown={() => setPanel("chat")}
            onClick={() => setPanel("chat")}
            sx={{
              backgroundColor: isDarkMode ? "#222" : "#E9E9E9",
              zIndex: 1,
            }}
            aria-label="toggle panel"
          >
            <ArrowBackIcon sx={{ color: isDarkMode ? "white" : "black" }} />
          </IconButton>
          {activePanelTitle && (
            <Box
              className="base-text"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                width: "calc(100% - 84px)",
                textAlign: "center",
                fontWeight: 600,
                fontSize: 15,
                color: isDarkMode ? "#F6F6F6" : "#1D1D1F",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                pointerEvents: "none",
              }}
            >
              {activePanelTitle}
            </Box>
          )}
        </Box>
      ) : (
        <IconButton
          onClick={() => setIsSidebarOpen((open) => !open)}
          sx={{ backgroundColor: isDarkMode ? "#222" : "#D9E2F8" }}
          aria-label="toggle drawer"
        >
          <MenuIcon sx={{ color: isDarkMode ? "white" : "black" }} />
        </IconButton>
      )}

      {panel == "chat" && (
        <Box
          sx={{
            ml: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.85,
          }}
        >
          {(!hasSubscription || preAccountUser) && (
            <Box sx={{ ml: "auto", mr: 1 }}>
              {preAccountUser ? (
                <Box
                  className="base-text"
                  sx={{
                    fontSize: isDesktop ? "13px" : "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0,
                    py: isDesktop ? "8px" : "6px",
                    width: "86px",
                    borderRadius: "26px",
                    color: isDarkMode ? "white" : "#5390E6",
                    border: isDesktop
                      ? "2px solid #5390E6"
                      : "1px solid #5390E6",
                    fontWeight: 500,
                    backgroundColor: "unset",
                    "&:hover": { cursor: "pointer" },
                  }}
                  onPointerDown={() => navigate("/register")}
                >
                  Sign In
                </Box>
              ) : (
                <Box
                  className="base-text"
                  sx={{
                    borderRadius: "999px",
                    border: `1px solid ${upgradePalette.border}`,
                    backgroundColor: upgradePalette.background,
                    px: "8px",
                    py: "5px",
                    display: "flex",
                    alignItems: "center",
                    maxWidth: "170px",
                    "&:hover": { cursor: "pointer" },
                  }}
                  onPointerDown={() => navigate("/plan-selection")}
                >
                  <Box
                    sx={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "999px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: "6px",
                      backgroundColor: upgradePalette.iconBg,
                      flexShrink: 0,
                    }}
                  >
                    <BoltRoundedIcon
                      sx={{ fontSize: "10px", color: upgradePalette.icon }}
                    />
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      letterSpacing: "0.1px",
                      color: upgradePalette.text,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      paddingX: "8px",
                      paddingY: "4px",
                    }}
                  >
                    {proUpgradeLabel}
                  </Box>
                  <ChevronRightIcon
                    sx={{
                      fontSize: "14px",
                      color: upgradePalette.text,
                      ml: "3px",
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
          {showMaxUpgradeButton && (
            <Box
              component="button"
              type="button"
              className="base-text"
              aria-label={`${maxUpgradeLabel} Incite MAX`}
              onClick={() => navigate(maxUpgradeHref)}
              sx={{
                appearance: "none",
                border: `1px solid ${maxUpgradePalette.border}`,
                backgroundColor: maxUpgradePalette.background,
                color: maxUpgradePalette.text,
                borderRadius: "999px",
                pl: isDesktop ? 1.05 : 0.85,
                pr: isDesktop ? 1.15 : 0.95,
                height: 33,
                display: "inline-flex",
                alignItems: "center",
                ml: "-4px",
                whiteSpace: "nowrap",
                fontWeight: 700,
                fontSize: isDesktop ? "10px" : "9px",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                transition:
                  "background-color 160ms ease, border-color 160ms ease",
                "&:hover": {
                  cursor: "pointer",
                  backgroundColor: maxUpgradePalette.hoverBackground,
                  borderColor: maxUpgradePalette.hoverBorder,
                },
              }}
            >
              <Box component="span" sx={{ lineHeight: 1, transform: "translateY(1px)" }}>
                {maxUpgradeLabel}
              </Box>
            </Box>
          )}
          {hasSubscription && (
            <FormControl
              size="small"
              sx={{
                minWidth:
                  showMaxUpgradeButton && !isDesktop ? 110 : 120,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "999px",
                  fontSize: isDesktop ? 12 : 11,
                  backgroundColor: isDarkMode ? "#020617" : "#FFFFFF",
                  color: isDarkMode ? "#E5E7EB" : "#111827",
                  paddingRight: "8px",
                  "& .MuiSelect-select": {
                    pt: 1.25,
                    pb: 1,
                    pl: 1.75,
                    pr: 3,
                  },
                  "& fieldset": {
                    borderColor: isDarkMode ? "#374151" : "#D1D5DB",
                  },
                  "&:hover fieldset": {
                    borderColor: isDarkMode ? "#9CA3AF" : "#111827",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3B82F6",
                  },
                },
                "& .MuiSelect-icon": {
                  color: isDarkMode ? "#9CA3AF" : "#6B7280",
                },
              }}
            >
              <Select
                value={selectedReasoningMode}
                onChange={onChangeReasoningMode}
                displayEmpty
                inputProps={{ "aria-label": "Reasoning time" }}
                renderValue={(selected) => (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.55,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selected === MAX_REASONING_MODE && (
                      <BoltRoundedIcon sx={{ fontSize: 14, color: "#F59E0B" }} />
                    )}
                    {REASONING_MODE_LABELS[selected] || REASONING_MODE_LABELS.standard}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? "#020617" : "#FFFFFF",
                      color: isDarkMode ? "#E5E7EB" : "#111827",
                      boxShadow: isDarkMode
                        ? "0 10px 30px rgba(0,0,0,0.7)"
                        : "0 10px 30px rgba(15,23,42,0.18)",
                      "& .MuiMenuItem-root": {
                        fontSize: 13,
                        transition: "background-color 140ms ease, color 140ms ease",
                      },
                      "& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-focusVisible": {
                        backgroundColor: isDarkMode
                          ? "rgba(55,65,81,0.56)"
                          : "rgba(15,23,42,0.07)",
                      },
                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: isDarkMode
                          ? "rgba(55,65,81,0.8)"
                          : "rgba(59,130,246,0.12)",
                      },
                      "& .MuiMenuItem-root.Mui-selected:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(55,65,81,0.92)"
                          : "rgba(59,130,246,0.18)",
                      },
                      "& .MuiMenuItem-root.Mui-selected.Mui-focusVisible": {
                        backgroundColor: isDarkMode
                          ? "rgba(55,65,81,0.9)"
                          : "rgba(59,130,246,0.16)",
                      },
                    },
                  },
                }}
              >
                <MenuItem value="standard">Fast</MenuItem>
                <MenuItem value="extended">Standard</MenuItem>
                <MenuItem value="deep">Deep Thinking</MenuItem>
                <MenuItem
                  value={MAX_REASONING_MODE}
                  sx={{
                    mt: 0.4,
                    borderTop: `1px solid ${isDarkMode ? "rgba(245,158,11,0.24)" : "rgba(217,119,6,0.2)"}`,
                    color: isDarkMode ? "#FCD34D" : "#92400E",
                    "&&:hover, &&.Mui-focusVisible": {
                      backgroundColor: isDarkMode
                        ? "rgba(245,158,11,0.12)"
                        : "rgba(245,158,11,0.1)",
                    },
                    "&&.Mui-selected": {
                      backgroundColor: isDarkMode
                        ? "rgba(245,158,11,0.16)"
                        : "rgba(245,158,11,0.14)",
                    },
                    "&&.Mui-selected:hover": {
                      backgroundColor: isDarkMode
                        ? "rgba(245,158,11,0.2)"
                        : "rgba(245,158,11,0.18)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      ml: "-2px",
                      width: "100%",
                    }}
                  >
                    <BoltRoundedIcon sx={{ fontSize: 15 }} />
                    <Box component="span">MAX Reasoning</Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}
          <IconButton
            aria-label="Open workspace"
            onClick={() => navigate("/chat-workspace")}
            sx={{
              backgroundColor: isDarkMode ? "#222" : "#D9E2F8",
              width: 36,
              height: 36,
              flex: "0 0 auto",
              ml: "-2px",
            }}
          >
            <DashboardCustomizeRoundedIcon
              sx={{ color: isDarkMode ? "white" : "black", fontSize: 19 }}
            />
          </IconButton>
          {!preAccountUser && (
            <IconWithBadge count={unreadNotificationCount}>
              <IconButton
                aria-label="notifications"
                onClick={() =>
                  setPanel((currentPanel) =>
                    currentPanel === "notifications" ? "chat" : "notifications",
                  )
                }
                sx={{
                  backgroundColor: isDarkMode ? "#222" : "#D9E2F8",
                  width: 36,
                  height: 36,
                  flex: "0 0 auto",
                  ml: "-2px",
                }}
              >
                <NotificationsNoneIcon
                  sx={{ color: isDarkMode ? "white" : "black" }}
                />
              </IconButton>
            </IconWithBadge>
          )}
        </Box>
      )}
    </Box>
  );
});

export default SidebarTopBar;
